// admin-hosts-view.tsx
//
// Vue RMM tabulaire affichée comme onglet workspace du cockpit (kind
// "admin-hosts"). Extrait de hosts-console.tsx avec une différence clé :
// connectHost() ne navigue plus vers /console/session mais délègue au cockpit
// via onConnectEnrolled (ouverture d'un onglet machine enrolled).
//
// Contient : table RMM dense (16 colonnes), filtres (client/site/OS/statut),
// tri colonnes, export CSV, approval/refus de hosts pending, suppression,
// présence temps réel via WS.

import { createSignal, createMemo, createEffect, onCleanup, For, Show } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { createQuery, useQueryClient } from "@tanstack/solid-query";
import { StatusDot } from "./ui";
import { resolveClientSite } from "./mock-tenants";
import { useWs } from "../shared/ws";
import { useConfirm } from "../shared/confirm-dialog";
import { useNotify } from "../shared/notify";
import {
  type HostItem,
  type SessionListResponseItem,
  type SortKey,
  type SortDir,
  type RawHostItem,
  normalizeHostItem,
  fetchSessionsRaw,
  relativeTime,
  pctBar,
  ramCell,
  displayHostLabel,
  exportCsv,
} from "./host-helpers";

// ─── Props ─────────────────────────────────────────────────────────────────

export interface AdminHostsViewProps {
  /** Appelée après ensure-session réussi : ouvre le host comme onglet cockpit. */
  onConnectEnrolled: (host: HostItem) => void;
}

// ─── SessionsStrip (chips sessions actives cross-host) ─────────────────────

function SessionsStrip(props: { sessions: () => SessionListResponseItem[]; onJoin: (code: string) => void }) {
  const activeSessions = createMemo(() => props.sessions().filter((s) => s.state !== "pending"));

  return (
    <div class="sessions-strip">
      <span class="sessions-strip-label">Sessions</span>
      <Show
        when={activeSessions().length > 0}
        fallback={<span style={{ "font-size": "11px", color: "var(--text-4)" }}>Aucune session active</span>}
      >
        <For each={activeSessions()}>
          {(s) => (
            <span
              class="session-chip"
              data-state={s.state ?? ""}
              onClick={() => props.onJoin(s.code)}
              title={`Host: ${s.hostId}`}
            >
              <span style={{ "font-weight": "600" }}>{s.code}</span>
              <span style={{ color: "var(--text-4)" }}>·</span>
              <span style={{ color: "var(--text-3)" }}>{s.hostId.slice(0, 10)}</span>
              <span
                style={{
                  "font-size": "10px",
                  "margin-left": "4px",
                  color: "var(--ok)",
                  background: "var(--ok-soft)",
                  padding: "0 4px",
                  "border-radius": "3px",
                }}
              >
                Rejoindre
              </span>
            </span>
          )}
        </For>
      </Show>
    </div>
  );
}

// ─── AdminHostsView ────────────────────────────────────────────────────────

export function AdminHostsView(props: AdminHostsViewProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const ws = useWs();
  const confirm = useConfirm();
  const notify = useNotify();

  // ── Filtres ──────────────────────────────────────────────────────────────
  const [search, setSearch] = createSignal("");
  const [filterClient, setFilterClient] = createSignal("");
  const [filterSite, setFilterSite] = createSignal("");
  const [filterOs, setFilterOs] = createSignal("");
  const [filterStatus, setFilterStatus] = createSignal("");

  // ── Tri ───────────────────────────────────────────────────────────────────
  const [sortKey, setSortKey] = createSignal<SortKey>("hostname");
  const [sortDir, setSortDir] = createSignal<SortDir>("asc");

  function handleSort(key: SortKey) {
    if (sortKey() === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  // ── Épinglés (partagés avec le cockpit via localStorage) ─────────────────
  function loadPinned(): string[] {
    try {
      return JSON.parse(localStorage.getItem("rws.pinnedHostIds") ?? "[]");
    } catch {
      return [];
    }
  }
  const [pinnedHostIds, setPinnedHostIds] = createSignal<string[]>(loadPinned());

  function togglePin(hostId: string) {
    setPinnedHostIds((prev) => {
      const next = prev.includes(hostId) ? prev.filter((id) => id !== hostId) : [...prev, hostId];
      localStorage.setItem("rws.pinnedHostIds", JSON.stringify(next));
      return next;
    });
  }

  // ── Données hosts ─────────────────────────────────────────────────────────
  const hostsQuery = createQuery<HostItem[]>(() => ({
    queryKey: ["hosts"],
    queryFn: async () => {
      const r = await fetch("/host/list");
      if (!r.ok) return [];
      const d = (await r.json()) as unknown;
      const body = d as { items?: unknown };
      const items = Array.isArray(body.items) ? body.items : Array.isArray(d) ? d : [];
      return items.map((h) => normalizeHostItem(h));
    },
    refetchInterval: 30_000,
    retry: false,
  }));

  const sessionsQuery = createQuery<SessionListResponseItem[]>(() => ({
    queryKey: ["sessions"],
    queryFn: fetchSessionsRaw,
    refetchInterval: 4000,
    retry: false,
  }));

  const pendingHostIdsFromSessions = createMemo(() => {
    const rows = sessionsQuery.data ?? [];
    const out = new Set<string>();
    for (const row of rows) {
      if (row.state !== "pending") continue;
      const hostId = String(row.hostId || "").trim();
      if (hostId) out.add(hostId);
    }
    return out;
  });

  const allHosts = createMemo<HostItem[]>(() =>
    (hostsQuery.data ?? []).map((host) => {
      if (!pendingHostIdsFromSessions().has(host.hostId)) return host;
      if (host.status === "pending") return host;
      return { ...host, status: "pending" as const };
    }),
  );

  function upsertHost(raw: unknown) {
    const patchRaw = raw as RawHostItem;
    const hostId = String(patchRaw.hostId ?? "");
    if (!hostId) return;
    queryClient.setQueryData<HostItem[]>(["hosts"], (old) => {
      const list = old ?? [];
      const idx = list.findIndex((h) => h.hostId === hostId);
      if (idx === -1) return [normalizeHostItem(raw), ...list];
      const next = list.slice();
      next[idx] = normalizeHostItem(raw, list[idx]);
      return next;
    });
  }

  // Présence temps réel via WS.
  createEffect(() => {
    if (ws.state.wsStatus === "open") {
      ws.send({ type: "host:presence:subscribe" });
    }
  });

  const offPresence = ws.on(
    (msg) => (msg as { type?: string }).type === "host_presence_update",
    (msg) => {
      const host = (msg as { host?: unknown }).host;
      if (host) upsertHost(host);
    },
  );

  onCleanup(() => {
    ws.send({ type: "host:presence:unsubscribe" });
    offPresence();
  });

  // ── Actions host ──────────────────────────────────────────────────────────
  const [connectingHostId, setConnectingHostId] = createSignal<string | null>(null);
  const [approvingHostId, setApprovingHostId] = createSignal<string | null>(null);
  const [hostCommandBusy, setHostCommandBusy] = createSignal<string | null>(null);

  // Connexion : ensure-session puis délégation au cockpit (onConnectEnrolled).
  // Le cockpit ouvre le host comme onglet enrolled via connectEnrolled().
  async function connectHost(host: HostItem) {
    setConnectingHostId(host.hostId);
    try {
      const res = await fetch(`/host/${host.hostId}/ensure-session`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${txt ? ` — ${txt.slice(0, 120)}` : ""}`);
      }
      const data = await res.json();
      const code = (data?.sessionCode as string) || (data?.code as string);
      if (!code) throw new Error("Pas de code de session retourné");
      // Rafraîchit le host pour récupérer le persistentSessionCode à jour,
      // puis délègue l'ouverture au cockpit.
      await hostsQuery.refetch();
      const updated = (hostsQuery.data ?? []).find((h) => h.hostId === host.hostId) ?? host;
      props.onConnectEnrolled({ ...updated, persistentSessionCode: code });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      notify.danger("Connexion échouée", { hint: msg });
    } finally {
      setConnectingHostId(null);
    }
  }

  async function approvePendingHost(hostId: string) {
    setApprovingHostId(hostId);
    try {
      const res = await fetch(`/host/${encodeURIComponent(hostId)}/ensure-session`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${txt ? ` — ${txt.slice(0, 120)}` : ""}`);
      }
      const data = await res.json().catch(() => ({}));
      const code = (data?.sessionCode as string | undefined) || (data?.code as string | undefined);
      notify.ok(code ? `Host approuvé, session ${code}` : "Host approuvé");
      await hostsQuery.refetch().catch(() => {});
    } catch (err) {
      notify.danger("Approbation échouée", { hint: err instanceof Error ? err.message : String(err) });
    } finally {
      setApprovingHostId(null);
    }
  }

  function sendHostCommand(host: HostItem, action: string, successMessage: string) {
    if (!ws.isOpen()) {
      notify.danger("WebSocket non connecté", { hint: "Impossible d'envoyer la commande hôte." });
      return;
    }
    setHostCommandBusy(`${host.hostId}:${action}`);
    ws.send({ type: "host:command", hostId: host.hostId, command: { action } });
    notify.info(successMessage);
    window.setTimeout(
      () => setHostCommandBusy((current) => (current === `${host.hostId}:${action}` ? null : current)),
      1200,
    );
  }

  function refreshHost(host: HostItem) {
    sendHostCommand(host, "refresh-components", "Refresh demandé : revalidation au prochain heartbeat.");
    void hostsQuery.refetch();
  }

  async function deleteHost(host: HostItem) {
    const ok = await confirm.confirm({
      title: `Supprimer ${displayHostLabel(host)}`,
      body: "L'hôte disparaîtra de la console. S'il est encore installé, il devra être réenregistré pour revenir.",
      level: "danger",
      entityName: host.hostId,
      confirmLabel: "Supprimer",
    });
    if (!ok) return;
    try {
      const res = await fetch(`/host/${encodeURIComponent(host.hostId)}`, {
        method: "DELETE",
        credentials: "include",
        headers: { accept: "application/json" },
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${txt ? ` — ${txt.slice(0, 120)}` : ""}`);
      }
      queryClient.setQueryData<HostItem[]>(["hosts"], (old) => (old ?? []).filter((h) => h.hostId !== host.hostId));
      notify.ok("Hôte supprimé.");
    } catch (err) {
      notify.danger("Suppression échouée", { hint: err instanceof Error ? err.message : String(err) });
    }
  }

  // ── Clients/sites distincts (pour les dropdowns) ──────────────────────────
  const distinctClients = createMemo(() => {
    const set = new Set<string>();
    for (const h of allHosts()) {
      set.add(resolveClientSite(h).client);
    }
    return [...set].sort();
  });

  const distinctSites = createMemo(() => {
    const set = new Set<string>();
    const client = filterClient();
    for (const h of allHosts()) {
      const cs = resolveClientSite(h);
      if (!client || cs.client === client) set.add(cs.site);
    }
    return [...set].sort();
  });

  // ── Filtrage + tri ────────────────────────────────────────────────────────
  const filteredHosts = createMemo(() => {
    const q = search().toLowerCase();
    const cli = filterClient();
    const sit = filterSite();
    const os = filterOs().toLowerCase();
    const st = filterStatus();

    let list = allHosts().filter((h) => {
      if (st && h.status !== st) return false;
      if (os && !`${h.system ?? ""} ${h.os ?? ""}`.toLowerCase().includes(os)) return false;
      const cs = resolveClientSite(h);
      if (cli && cs.client !== cli) return false;
      if (sit && cs.site !== sit) return false;
      if (q) {
        const hay = [
          displayHostLabel(h),
          h.hostname,
          h.hostId,
          h.system ?? "",
          h.systemVersion ?? "",
          h.os ?? "",
          h.publicIp ?? "",
          h.privateIp ?? "",
          h.ip ?? "",
          h.user ?? "",
          ...(h.tags ?? []),
        ]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    const key = sortKey();
    const dir = sortDir() === "asc" ? 1 : -1;

    list = [...list].sort((a, b) => {
      let va: string | number = "";
      let vb: string | number = "";
      if (key === "client") {
        va = resolveClientSite(a).client;
        vb = resolveClientSite(b).client;
      } else if (key === "site") {
        va = resolveClientSite(a).site;
        vb = resolveClientSite(b).site;
      } else {
        va = (a[key] as string | number) ?? "";
        vb = (b[key] as string | number) ?? "";
      }
      if (typeof va === "number" && typeof vb === "number") return dir * (va - vb);
      return dir * String(va).localeCompare(String(vb));
    });

    return list;
  });

  function thAttr(key: SortKey) {
    return sortKey() === key ? sortDir() : undefined;
  }

  // Rejoindre une session existante : navigue vers le cockpit avec le code.
  // (Le cockpit lit ?s= au montage pour ouvrir l'onglet.)
  function joinSession(code: string) {
    navigate(`/console/session?s=${code}`);
  }

  return (
    <div class="admin-hosts-view">
      {/* ── Barre de filtres ─────────────────────────────────────── */}
      <div class="filter-bar">
        <div class="filter-input">
          <span style={{ color: "var(--text-4)", "font-size": "12px" }}>⌕</span>
          <input
            type="text"
            placeholder="Rechercher hostname, IP publique, système, tags…"
            value={search()}
            onInput={(e) => setSearch(e.currentTarget.value)}
          />
        </div>

        <select
          class="filter-select"
          value={filterClient()}
          onChange={(e) => {
            setFilterClient(e.currentTarget.value);
            setFilterSite("");
          }}
        >
          <option value="">Tous les clients</option>
          <For each={distinctClients()}>{(c) => <option value={c}>{c}</option>}</For>
        </select>

        <select class="filter-select" value={filterSite()} onChange={(e) => setFilterSite(e.currentTarget.value)}>
          <option value="">Tous les sites</option>
          <For each={distinctSites()}>{(s) => <option value={s}>{s}</option>}</For>
        </select>

        <select class="filter-select" value={filterOs()} onChange={(e) => setFilterOs(e.currentTarget.value)}>
          <option value="">Tous les systèmes</option>
          <option value="windows">Windows</option>
          <option value="macos">macOS</option>
          <option value="linux">Linux</option>
          <option value="ubuntu">Ubuntu</option>
        </select>

        <select class="filter-select" value={filterStatus()} onChange={(e) => setFilterStatus(e.currentTarget.value)}>
          <option value="">Tous les états</option>
          <option value="online">En ligne</option>
          <option value="idle">Inactif</option>
          <option value="offline">Hors ligne</option>
          <option value="pending">En attente</option>
        </select>

        <span class="filter-count">{filteredHosts().length} hosts</span>

        <div class="filter-spacer" />

        <button class="btn" type="button" onClick={() => hostsQuery.refetch()} title="Rafraîchir">
          ↺ Rafraîchir
        </button>

        <button class="btn" type="button" onClick={() => exportCsv(filteredHosts())} title="Exporter en CSV">
          ⬇ CSV
        </button>
      </div>

      {/* ── Sessions actives ─────────────────────────────────────── */}
      <SessionsStrip sessions={() => sessionsQuery.data ?? []} onJoin={joinSession} />

      {/* ── Table RMM ────────────────────────────────────────────── */}
      <div class="rmm-table-wrap">
        <Show
          when={!hostsQuery.isLoading}
          fallback={
            <div style={{ padding: "32px 16px", color: "var(--text-4)", "font-size": "12px" }}>Chargement…</div>
          }
        >
          <table class="rmm-table">
            <thead>
              <tr>
                <th style={{ width: "24px", cursor: "default" }} />
                <th data-sorted={thAttr("hostname")} onClick={() => handleSort("hostname")}>
                  Host
                </th>
                <th data-sorted={thAttr("client")} onClick={() => handleSort("client")}>
                  Client
                </th>
                <th data-sorted={thAttr("site")} onClick={() => handleSort("site")}>
                  Site
                </th>
                <th data-sorted={thAttr("system")} onClick={() => handleSort("system")}>
                  Système
                </th>
                <th data-sorted={thAttr("systemVersion")} onClick={() => handleSort("systemVersion")}>
                  Version OS
                </th>
                <th data-sorted={thAttr("user")} onClick={() => handleSort("user")}>
                  User
                </th>
                <th data-sorted={thAttr("publicIp")} onClick={() => handleSort("publicIp")}>
                  IP publique
                </th>
                <th data-sorted={thAttr("privateIp")} onClick={() => handleSort("privateIp")}>
                  IP locale
                </th>
                <th data-sorted={thAttr("status")} onClick={() => handleSort("status")}>
                  État
                </th>
                <th data-sorted={thAttr("cpu")} onClick={() => handleSort("cpu")}>
                  CPU%
                </th>
                <th data-sorted={thAttr("ram")} onClick={() => handleSort("ram")}>
                  RAM
                </th>
                <th data-sorted={thAttr("agentVersion")} onClick={() => handleSort("agentVersion")}>
                  Agent
                </th>
                <th data-sorted={thAttr("lastSeen")} onClick={() => handleSort("lastSeen")}>
                  Heartbeat
                </th>
                <th style={{ cursor: "default" }}>Tags</th>
                <th style={{ cursor: "default" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              <Show
                when={filteredHosts().length > 0}
                fallback={
                  <tr>
                    <td colspan="16" style={{ "text-align": "center", color: "var(--text-4)", padding: "32px 0" }}>
                      Aucun host correspondant aux filtres
                    </td>
                  </tr>
                }
              >
                <For each={filteredHosts()}>
                  {(h) => {
                    const cs = resolveClientSite(h);
                    const isPinned = () => pinnedHostIds().includes(h.hostId);
                    const isPending = () => h.status === "pending";
                    return (
                      <tr>
                        {/* ★ pin */}
                        <td>
                          <button
                            type="button"
                            title={isPinned() ? "Désépingler" : "Épingler"}
                            onClick={() => togglePin(h.hostId)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: isPinned() ? "var(--warn)" : "var(--text-4)",
                              "font-size": "13px",
                              padding: "0",
                              "line-height": "1",
                            }}
                          >
                            ★
                          </button>
                        </td>

                        {/* Host */}
                        <td>
                          <span class="rmm-table-host-id" title={h.hostId}>
                            {displayHostLabel(h)}
                          </span>
                          <Show when={h.displayName && h.hostname && h.displayName !== h.hostname}>
                            <span class="rmm-table-host-sub">{h.hostname}</span>
                          </Show>
                        </td>

                        {/* Client */}
                        <td style={{ color: "var(--text-2)" }}>{cs.client}</td>

                        {/* Site */}
                        <td style={{ color: "var(--text-3)" }}>{cs.site}</td>

                        {/* Système */}
                        <td style={{ color: "var(--text-3)" }} title={h.os ?? ""}>
                          {h.system ?? h.os ?? "—"}
                        </td>

                        {/* Version OS */}
                        <td style={{ color: "var(--text-4)" }} title={h.os ?? h.systemVersion ?? ""}>
                          {h.systemVersion ?? "—"}
                        </td>

                        {/* User */}
                        <td style={{ "font-family": "var(--font-mono)", "font-size": "11px" }}>{h.user ?? "—"}</td>

                        {/* IP publique */}
                        <td style={{ "font-family": "var(--font-mono)", "font-size": "11px" }}>{h.publicIp ?? "—"}</td>

                        {/* IP locale */}
                        <td style={{ "font-family": "var(--font-mono)", "font-size": "11px" }}>{h.privateIp ?? "—"}</td>

                        {/* État */}
                        <td>
                          <span style={{ display: "inline-flex", "align-items": "center", gap: "5px" }}>
                            <StatusDot status={h.status} />
                            <span style={{ "font-size": "11px", color: "var(--text-3)" }}>{h.status}</span>
                          </span>
                        </td>

                        {/* CPU% */}
                        <td>{pctBar(h.cpu, "CPU")}</td>

                        {/* RAM */}
                        <td>{ramCell(h)}</td>

                        {/* Agent version */}
                        <td style={{ "font-family": "var(--font-mono)", "font-size": "11px", color: "var(--text-4)" }}>
                          {h.agentVersion ?? "—"}
                        </td>

                        {/* Heartbeat */}
                        <td style={{ "font-size": "11px", color: "var(--text-4)" }}>{relativeTime(h.lastSeen)}</td>

                        {/* Tags */}
                        <td>
                          <span style={{ display: "inline-flex", gap: "3px", "flex-wrap": "nowrap" }}>
                            <For
                              each={(h.tags ?? [])
                                .filter((t) => !t.startsWith("client:") && !t.startsWith("site:"))
                                .slice(0, 3)}
                            >
                              {(tag) => (
                                <span class="tag" style={{ "font-size": "10px", padding: "0 4px" }}>
                                  {tag}
                                </span>
                              )}
                            </For>
                          </span>
                        </td>

                        {/* Actions */}
                        <td>
                          <div class="host-row-actions">
                            <Show
                              when={!isPending()}
                              fallback={
                                <button
                                  class="btn btn--primary btn--xs"
                                  type="button"
                                  onClick={() => approvePendingHost(h.hostId)}
                                  disabled={approvingHostId() === h.hostId}
                                  title="Approuver l'enrôlement de ce host"
                                >
                                  {approvingHostId() === h.hostId ? "Approbation…" : "Approuver"}
                                </button>
                              }
                            >
                              <button
                                class="btn btn--primary btn--xs"
                                type="button"
                                onClick={() => connectHost(h)}
                                disabled={h.status === "offline" || connectingHostId() === h.hostId}
                              >
                                {connectingHostId() === h.hostId ? "Connexion…" : "Connecter"}
                              </button>
                            </Show>
                            <button
                              class="btn btn--xs"
                              type="button"
                              onClick={() => refreshHost(h)}
                              disabled={hostCommandBusy() === `${h.hostId}:refresh-components`}
                              title="Forcer le re-téléchargement des composants (Agent + Helper)"
                            >
                              {hostCommandBusy() === `${h.hostId}:refresh-components` ? "Refresh…" : "Refresh"}
                            </button>
                            <button class="btn btn--danger btn--xs" type="button" onClick={() => deleteHost(h)}>
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }}
                </For>
              </Show>
            </tbody>
          </table>
        </Show>
      </div>

      {/* ── Footer ───────────────────────────────────────────────── */}
      <div class="rmm-table-footer">
        <span>
          {filteredHosts().length} / {allHosts().length} hosts
        </span>
        <Show when={hostsQuery.isError}>
          <span style={{ color: "var(--danger)" }}>Erreur de chargement — données indisponibles</span>
        </Show>
        <span style={{ color: "var(--text-4)" }}>
          Tri : {sortKey()} {sortDir()}
        </span>
      </div>
    </div>
  );
}
